import { Box, Checkbox, FormControlLabel, FormGroup, Menu, MenuItem } from '@mui/material'
import { ActivitiesIcon, ClearIcon, FilterIcon, MessagingFilledIcon, ScheduleIcon, VehicleIcon } from 'components/icons'
import useDeviceSize, { DeviceSize } from 'hooks/useDeviceSize'
import React, { useState } from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { baseURL, isPaidTransportFeatureEnabled } from 'utils/constants'
import './index.css'

const MenuFilterComponent = ({
  icon,
  label,
  selectedOptions = new Set(),
  options,
  onChange,
  testid,
}: {
  icon?: React.ReactNode
  label?: string
  options: Set<string>
  selectedOptions?: Set<string>
  onChange: (selectedOptions: Set<string>) => void
  testid?: string
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  // const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set())

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClear = () => {
    const a: Set<string> = new Set()
    // setSelectedOptions(a)
    onChange(a)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = event.target
    const a: Set<string> = new Set(selectedOptions)
    checked ? a.add(name) : a.delete(name)
    // setSelectedOptions(a)
    onChange(a)
  }

  const filterCount: number = selectedOptions.size

  return (
    <div data-testid={`${testid}-div`} style={{ fontSize: '0.875rem' }}>
      <button
        type='button'
        className='flex items-center justify-center h-10 rounded-lg border border-[#E5E5E5] bg-white hover:bg-gray-50 focus:outline-none relative px-0 sm:px-2'
        onClick={handleClick}
        data-testid={`${testid}-icon-btn`}
      >
        {icon ?? <FilterIcon />}
        {label && <span className='ml-1 whitespace-nowrap'>{label}</span>}
        {filterCount > 0 && (
          <>
            <span className='ml-1 bg-custom-yellow rounded-xl px-1 text-xs'>{filterCount}</span>
            <ClearIcon onClick={handleClear} />
          </>
        )}
      </button>
      <Menu
        id='simple-menu'
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        MenuListProps={{ dense: true }}
        slotProps={{
          paper: {
            sx: {
              maxHeight: 300, // Set max height for the dropdown
              overflowY: 'auto', // Allow scrolling if content overflows
            },
          },
        }}
      >
        <FormGroup>
          {Array.from(options).map((v: string) => (
            <MenuItem key={v} dense>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedOptions.has(v)}
                    onChange={handleChange}
                    name={v}
                    size='small'
                    sx={{
                      '&.Mui-checked': {
                        color: '#EBD76F', // Use your custom hex color here
                      },
                      '& .MuiSvgIcon-root': {
                        fill: selectedOptions.has(v) ? '#EBD76F' : '#666666', // Fill color with hex when checked
                      },
                    }}
                  />
                }
                label={<span style={{ fontSize: '0.875rem' }}>{v}</span>} // Smaller label
              />
            </MenuItem>
          ))}
        </FormGroup>
      </Menu>
    </div>
  )
}

const SimpleDatePicker: React.FC<{
  selectedDate?: string
  onChange: (date?: string) => void
}> = ({ selectedDate, onChange }) => {
  const handleDateSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value || undefined)
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        height: '40px',
        width: '40px',
        borderRadius: '8px',
        border: '1px solid #E5E5E5',
        cursor: 'pointer',
      }}
      data-testid='simple-calender-icon'
    >
      <input
        type='date'
        value={selectedDate || ''}
        onChange={handleDateSelect}
        style={{
          height: '100%',
          width: '100%',
          borderRadius: '8px',
          opacity: 0.01 /* Very small opacity to make it clickable but visually hidden */,
          cursor: 'pointer',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
        data-testid='calender-testid'
      />
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      >
        <svg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'>
          <path
            d='M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z'
            fill='#666666'
          />
        </svg>
      </div>
    </div>
  )
}

export type FilterSelectionProps = {
  scheduleSelections?: Set<string>
  vehicleSelections?: Set<string>
  activitySelections?: Set<string>
  dateSelection?: string
  clearAllSelections?: boolean
}

const FilterComponent = (props: {
  name?: string
  adminLink?: string
  schedules?: Set<string>
  vehicles?: Set<string>
  activities?: Set<string>
  selectedSchedules?: Set<string>
  selectedVehicles?: Set<string>
  selectedActivities?: Set<string>
  selectedDate?: string
  onSave: (props: FilterSelectionProps) => void
}) => {
  const navigate: NavigateFunction = useNavigate()
  const deviceSize: DeviceSize = useDeviceSize()

  // eslint-disable-next-line
  const {
    name,
    adminLink,
    schedules,
    vehicles,
    activities,
    selectedSchedules,
    selectedVehicles,
    selectedActivities,
    selectedDate,
    onSave,
  } = props
  const [isValidLogo, setIsValidLogo] = useState(true)

  // handlers
  // const handleClear = () => onSave({ clearAllSelections: true })
  const handleSave = ({
    scheduleSelections,
    vehicleSelections,
    activitySelections,
    dateSelection,
  }: Partial<FilterSelectionProps>) => {
    onSave({ scheduleSelections, vehicleSelections, activitySelections, dateSelection })
  }
  const handleScheduleChange = (newSelections: Set<string>) => {
    handleSave({ scheduleSelections: newSelections })
  }
  const handleVehicleChange = (newSelections: Set<string>) => {
    handleSave({ vehicleSelections: newSelections })
  }
  const handleActivityChange = (newSelections: Set<string>) => {
    handleSave({ activitySelections: newSelections })
  }
  const handleDateChange = (date?: string) => {
    handleSave({ dateSelection: date })
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        padding: 0,
        margin: 0,
        width: '100%',
      }}
    >
      {/***************SCHOOL LOGO/RESOURCES **********/}
      {deviceSize !== DeviceSize.xs && deviceSize !== DeviceSize.sm && (
        <div className='flex items-center gap-1 max-w-[20%]'>
          {isValidLogo && (
            <a href={adminLink} target='_blank' rel='noopener noreferrer'>
              <img
                src={`${baseURL}/media/communities/${name}/photo-96x96.jpg`}
                alt='School Logo'
                className='w-12 h-12 rounded-full shadow-sm object-cover'
                onError={() => setIsValidLogo(false)} // Mark as invalid if loading fails
              />
            </a>
          )}
          <div
            className='flex flex-col min-w-0 flex-1 cursor-pointer'
            onClick={() => {
              if (adminLink) {
                window.open(adminLink, '_blank')
              }
            }}
          >
            <h2 className='text-md text-gray-800 font-semibold truncate'>{name}</h2>
            <p className='text-sm text-gray-600 underline truncate'>Family Resources</p>
          </div>
        </div>
      )}

      {/***************AFTERNOON DATEPICKER**********/}
      {selectedDate && <SimpleDatePicker selectedDate={selectedDate} onChange={handleDateChange} />}

      {/*************** ACTIVITIES FILTER**********/}
      {activities && activities.size > 0 && (
        <MenuFilterComponent
          icon={<ActivitiesIcon color='#fff' size='32' />}
          options={activities}
          selectedOptions={selectedActivities}
          onChange={handleActivityChange}
          testid='activity-filter'
        />
      )}

      {/***************SCHOOL SCHEDULE FILTER**********/}
      {schedules && schedules.size > 1 && (
        <MenuFilterComponent
          icon={<ScheduleIcon color='#fff' size='32' />}
          options={schedules}
          selectedOptions={selectedSchedules}
          onChange={handleScheduleChange}
          testid='group-filter'
        />
      )}

      {/************** USE PAID TRANSPORT **********/}
      {isPaidTransportFeatureEnabled && vehicles && vehicles.size > 0 && (
        <MenuFilterComponent
          icon={<VehicleIcon color='#fff' size='32' />}
          options={vehicles}
          selectedOptions={selectedVehicles}
          onChange={handleVehicleChange}
          testid='group-filter'
        />
      )}

      {/***************CLEAR ALL FILTERS**********/}
      {/* FIXME: Clear should be hidden until changes are detected */}
      {/* {deviceSize !== DeviceSize.xs && deviceSize !== DeviceSize.sm ? (
        <button
          className='flex items-center h-10 rounded-lg border border-[#E5E5E5] bg-white hover:bg-gray-50 focus:outline-none relative px-2'
          onClick={handleClear}
        >
          <ClearIcon />
          <span className='ml-1 text-sm whitespace-nowrap'>Clear All</span>
        </button>
      ) : (
        <ClearIcon onClick={handleClear} />
      )} */}

      {/***************MESSAGING BUTTON**********/}
      <button
        className='flex w-10 h-10 justify-center items-center rounded-lg bg-[#F0E977]'
        aria-label='New Group'
        onClick={() => navigate(`/messaging`)}
        style={{ marginLeft: 'auto' }} // Ensures the button is right-aligned
      >
        <MessagingFilledIcon />
      </button>
    </Box>
  )
}

export default FilterComponent
